import cx from 'classnames'

import { useEffect, useState } from 'react'
import Styles from './ZapierIntegration.module.scss'

import { APIKey } from '../../ApiKey/ApiKeySection'
import { useApiKeys } from '../../ApiKey/hooks/queries/useApiKeys'
import { ApiKeyClient } from 'api/clients/ApiKeyClient'
import { Modal } from 'almanac-ui/Modal'
import { APICredential, PublishDocSubscription } from 'types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BLAZE_ZAPIER_INTEGRATION_HELP_CENTER_URL } from 'paths'
import { ArrowUpRightSquareContained, AlertTriangle } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { GraduationCap } from 'blaze-ui/icons'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useQuery } from 'react-query'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ArticleClient } from 'api/clients/ArticleClient'
import { ZapierLogo } from 'almanac-ui/icons/24'
import { IntegrationHelpButton } from 'components/IntegrationHelpButton/IntegrationHelpButton'
import { useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ZapierTargetsPicker } from './ZapierTargetsPicker'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface Props {
  articleIdToPublish?: string
  inDrawer?: boolean
}

export const ZapierIntegration = ({ articleIdToPublish, inDrawer }: Props) => {
  const { isFetched, data } = useApiKeys()
  const workspace = useCurrentWorkspace()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const [zapierApiCredential, setZapierApiCredential] = useState<APICredential | null>(null)
  const subscriptionQuery = usePublishDocSubscriptions({ workspaceId: currentWorkspaceId })
  const openWindowSafely = useOpenWindowSafely()
  const { isMobile } = useResponsiveDesign()
  const { closeModal } = useModals()
  const isZapierPickerEnabled = useFlipperFlag('zapier_v2')

  useEffect(() => {
    const scriptUrl = 'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js'
    const styleUrl = 'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css'

    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      const script = document.createElement('script')
      script.type = 'module'
      script.src = scriptUrl
      document.head.appendChild(script)
    }

    if (!document.querySelector(`link[href="${styleUrl}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = styleUrl
      document.head.appendChild(link)
    }
  }, [])

  const fetchAndSetKey = async () => {
    const res = await ApiKeyClient.create('zapier')
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setZapierApiCredential(res.data)
    }
  }

  const exportViaZapier = (sub: PublishDocSubscription) => {
    if (!articleIdToPublish) {
      showErrorMessageToast('Unable to export via Zapier')
      return
    }

    ArticleClient.publishArticle(articleIdToPublish, [sub.id])
    showToastMessage(`The document has been published through Zapier using ${sub.subscription_name}`)
  }

  useEffect(() => {
    if (isFetched && data?.api_credentials) {
      const zapierKey = data?.api_credentials.find((cred) => cred.intended_use === 'zapier')

      if (zapierKey) {
        setZapierApiCredential(zapierKey)
      } else {
        fetchAndSetKey()
      }
    }
  }, [isFetched, data?.api_credentials])

  // "Has Zaps" drives the "Your Zaps should show here..." empty state. When the
  // picker is enabled, we consider both the legacy publish_doc subscriptions
  // AND the new ExternalIntegration(provider:'zapier') rows from REST Hooks.
  // When disabled, only the legacy WorkspaceSubscriptions are the source of
  // truth — any ExternalIntegration(zapier) rows are either leftover state
  // from when the flag was on, or unrelated to this surface.
  const { data: externalIntegrationsData } = useExternalIntegrations()
  const hasLegacyZapSubscriptions =
    subscriptionQuery.isFetched && (subscriptionQuery?.data?.subscriptions?.length || 0) > 0
  const hasScheduledPostZaps =
    isZapierPickerEnabled &&
    (externalIntegrationsData?.external_integrations ?? []).some((i) => i.provider === ExternalIntegrationEnum.Zapier)
  const hasZaps = hasLegacyZapSubscriptions || hasScheduledPostZaps

  type ZapsMode = 'publishButtons' | 'legacyEmbed' | 'hidden'
  const zapsMode: ZapsMode = (() => {
    if (articleIdToPublish) return 'publishButtons'
    if (!isZapierPickerEnabled) return 'legacyEmbed'
    return 'hidden'
  })()

  const renderZapsSection = () => {
    switch (zapsMode) {
      case 'publishButtons':
        return (
          <div className={Styles.zapsPublishContainer}>
            <div className={Styles.zapsPublishHeading}>
              <Heading level={5}>Your Connected Zaps</Heading>
            </div>
            {subscriptionQuery?.data?.subscriptions.map((sub) => (
              <Button
                key={sub.id}
                variant="secondary"
                frontIcon={ZapierLogo}
                fullWidth
                className={Styles.zapPublish}
                onPress={() => exportViaZapier(sub)}
              >
                {sub.subscription_name}
              </Button>
            ))}
          </div>
        )
      case 'legacyEmbed':
        return (
          <div className={Styles.zapsContainer}>
            {/* @ts-ignore: zapier-full-experience is a web component loaded from a script tag in index.html */}
            <zapier-full-experience
              client-id="ikAlBx0Qg3IvNZR2PxkK8Up0JqFNyGCMTqQQZDqL"
              theme="light"
              intro-copy-display="hide"
              app-selection-display="hide"
              zap-limit="0"
              zap-create-from-scratch-display="hide"
            />
          </div>
        )
      case 'hidden':
        return null
    }
  }

  const content = (
    <div className={Styles.container}>
      <div className={cx(Styles.topSection, { [Styles.gap16]: hasZaps })}>
        <>
          {isMobile || inDrawer ? (
            <Modal.Header
              title="Zapier Integration"
              className={Styles.noLeftPadding}
              actions={
                <div className={Styles.headerActions}>
                  <ButtonLink to="https://zapier.com/app/connections" target="_blank" variant="ghost">
                    Open Zapier
                  </ButtonLink>
                </div>
              }
            />
          ) : (
            <div className={Styles.logoAndButton}>
              <>
                <Modal.Header
                  title="Zapier Integration"
                  className={Styles.noLeftPadding}
                  actions={<IntegrationHelpButton size="sm" integration="Zapier" />}
                />
              </>
            </div>
          )}
          {workspace?.is_virtual_marketer && !isZapierPickerEnabled && (
            <div className={Styles.warningBanner}>
              <AlertTriangle size={20} color="#FFAE00" />
              <Text variant="primary">Zapier does not integrate with your Campaigns</Text>
            </div>
          )}
          <div>
            <p className={Styles.description}>
              You can export to CMS, social media or ads managers. You can also automate generating content based on
              external triggers, such as when a row is added to an Airtable base.
            </p>
          </div>
        </>
      </div>
      {hasZaps && renderZapsSection()}
      {!hasZaps && (
        <div className={Styles.setupInstructions}>
          <div className={Styles.innerContent}>
            <Heading level={5}>Use the API key below to connect with Zapier</Heading>
            <div className={Styles.innterText}>
              Your Zaps should show here instead of this message. If you still can’t see your zaps here, review our
              detailed guide
            </div>
            <div className={Styles.connectButton}>
              <Button
                variant="secondary"
                frontIcon={GraduationCap}
                size="sm"
                onPress={() => openWindowSafely(BLAZE_ZAPIER_INTEGRATION_HELP_CENTER_URL)}
              >
                See Connection Guide
              </Button>
            </div>
          </div>
        </div>
      )}
      {isZapierPickerEnabled && <ZapierTargetsPicker />}
      {zapierApiCredential && (
        <div className={Styles.apiCredentials}>
          <Text variant="label">Blaze API Key</Text>
          <APIKey {...zapierApiCredential} />
        </div>
      )}
    </div>
  )

  if (isMobile) {
    return <div className={cx(Styles.mobileRoot, { [Styles.inDrawer]: inDrawer })}>{content}</div>
  }

  return (
    <Modal.Root size="sm" blaze>
      {content}
      {!isMobile && (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={closeModal} size="lg">
              Back
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <ButtonLink
              to="https://zapier.com/app/connections"
              target="_blank"
              size="lg"
              endIcon={ArrowUpRightSquareContained}
            >
              {isMobile ? '' : 'Open Zapier'}
            </ButtonLink>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </Modal.Root>
  )
}

export function usePublishDocSubscriptions({ workspaceId }: { workspaceId: number }) {
  return useQuery(
    ['publish-doc-subscriptions'],
    async () => {
      const res = await WorkspaceClient.getPublishDocWorkspaceSubscriptions({ workspaceId })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return res.data
    },
    {
      initialData: () => ({ subscriptions: [] }),
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  )
}
